import type { ListBoxProps } from '@repo/foundations';
import type { Dispatch, SecodaEditorComponentProps } from '@repo/secoda-editor';
import type { MarkdownSerializerState } from '@repo/secoda-editor/lib/markdown/serializer';
import type { NodeOptions } from '@repo/secoda-editor/nodes/Node';
import ReactNode from '@repo/secoda-editor/nodes/ReactNode';
import queryBlocksRule from '@repo/secoda-editor/rules/queryblocks';
import type { ParseSpec } from 'prosemirror-markdown';
import type { NodeSpec, Node as ProsemirrorNode } from 'prosemirror-model';
import { type EditorState } from 'prosemirror-state';
import { useCallback } from 'react';
import { v4 } from '../../../../../utils/uuid/v4';
import { IntegrationPicker } from '../components/IntegrationPicker';
import { QueryBlockContainerWrapper } from './QueryBlockContainerWrapper';

export default class QueryBlock extends ReactNode {
	get name() {
		return 'query_block';
	}

	get schema(): NodeSpec {
		return {
			attrs: {
				id: {
					default: '', // This value is shared across all new nodes, so the `uuid()` call cannot be here.
				},
				integrationId: {
					default: '',
				},
			},
			group: 'block',
			defining: true,
			atom: true,
			parseDOM: [
				{
					preserveWhitespace: 'full',
					tag: 'div.queryblock',
					getAttrs: (dom: HTMLElement | string) => ({
						id:
							typeof dom === 'string' ? v4() : (dom.getAttribute('id') ?? v4()),
					}),
				},
			],
			toDOM: (node) => [
				'div',
				{
					...node.attrs,
					class: 'queryblock',
					id: node.attrs.id,
				},
				node.attrs.id,
			],
		};
	}

	toMarkdown(state: MarkdownSerializerState, node: ProsemirrorNode) {
		const { id } = node.attrs;
		if (id) {
			state.write(`\n\n;;;${id};;;\n\n`);
			state.closeBlock(node);
		}
	}

	parseMarkdown(): ParseSpec {
		return {
			block: 'query_block',
			getAttrs: (tok) => ({ id: tok.attrs?.[0][1].split(';;;')[1] }),
		};
	}

	get rulePlugins() {
		return [queryBlocksRule];
	}

	commands({ type }: NodeOptions) {
		return (attrs?: Record<string, unknown>) =>
			(state: EditorState, dispatch: Dispatch) => {
				let id = attrs?.id;
				if (!id) {
					id = v4();
				}

				dispatch(
					state.tr.insert(
						state.selection.from,
						type.create({ ...(attrs ?? {}), id })
					)
				);

				return true;
			};
	}

	component = (props: SecodaEditorComponentProps) => {
		const { getPos } = props;
		const handleChangeId = useCallback(
			(id: string) => {
				this.editor?.view?.dispatch(
					this.editor.view.state.tr.setNodeAttribute(getPos(), 'id', id)
				);
			},
			[getPos]
		);

		return (
			<QueryBlockContainerWrapper
				{...props}
				key={props.isEditable ? 'editable' : 'readOnly'}
				onChangeId={handleChangeId}
				readOnly={!props.isEditable}
				onShowToast={this.editor?.options?.onShowToast}
				onTrackEvent={this.editor?.options?.onTrackEvent}
				shouldRenderPlaceholder={this.editor?.options?.template}
			/>
		);
	};

	preselectionWidget(props: {
		onClose: () => void;
		targetRef: ListBoxProps['targetRef'];
	}) {
		return (
			<IntegrationPicker
				onClose={props.onClose}
				editor={this.editor}
				targetRef={props.targetRef}
			/>
		);
	}
}
